// TripSummaryCard - Semantic hero that creates trust and clarity
// Shows trip benefits, not pretty pictures

import { useTranslation } from 'react-i18next';
import { TripContent } from '../../services/api';
import './TripSummaryCard.css';

interface TripSummaryCardProps {
    content: TripContent;
    className?: string;
}

export default function TripSummaryCard({ content, className = '' }: TripSummaryCardProps) {
    const { t } = useTranslation();

    // Calculate trip stats
    const totalDays = content.cities.reduce((acc, city) => acc + city.days.length, 0);
    const totalActivities = content.cities.reduce(
        (acc, city) => acc + city.days.reduce((dayAcc, day) => dayAcc + day.activities.length, 0),
        0
    );
    const cityNames = content.cities.map(c => c.name);
    const isMultiCity = cityNames.length > 1;

    // Determine trip type description
    const getTripTypeLabel = () => {
        if (totalDays === 1) return t('tripSummary.one_day_plan');
        if (totalDays <= 3) return t('tripSummary.short_trip');
        if (totalDays <= 7) return t('tripSummary.week_trip');
        return t('tripSummary.extended_trip');
    };

    return (
        <div className={`trip-summary-card ${className}`}>
            {/* Main headline */}
            <div className="summary-headline">
                <span className="summary-type">{getTripTypeLabel()}</span>
                <h2 className="summary-title">
                    {isMultiCity 
                        ? cityNames.slice(0, 2).join(' & ') + (cityNames.length > 2 ? ` +${cityNames.length - 2}` : '')
                        : cityNames[0]
                    }
                </h2>
            </div>

            {/* Benefits checkmarks */}
            <div className="summary-benefits">
                <div className="benefit-item">
                    <CheckIcon />
                    <span>{t('tripSummary.benefit_optimized')}</span>
                </div>
                <div className="benefit-item">
                    <CheckIcon />
                    <span>{t('tripSummary.benefit_no_backtrack')}</span>
                </div>
                <div className="benefit-item">
                    <CheckIcon />
                    <span>{t('tripSummary.benefit_grouped')}</span>
                </div>
            </div>

            {/* Micro-chips */}
            <div className="summary-chips">
                <span className="chip">
                    <LocationIcon />
                    {t('tripSummary.chip_center_based')}
                </span>
                <span className="chip">
                    <ClockIcon />
                    {totalDays} {totalDays === 1 ? t('tripSummary.day') : t('tripSummary.days')} · {totalActivities} {t('tripSummary.activities')}
                </span>
                <span className="chip">
                    <WalkIcon />
                    {t('tripSummary.chip_walkable')}
                </span>
            </div>

            {/* Social proof */}
            <div className="summary-proof">
                <span className="proof-item">
                    <SparkleIcon />
                    {t('tripSummary.proof_generated')}
                </span>
                <span className="proof-item">
                    <AIIcon />
                    {t('tripSummary.proof_optimized')}
                </span>
            </div>
        </div>
    );
}

// =============================================================================
// ICONS
// =============================================================================

function CheckIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12" />
        </svg>
    );
}

function LocationIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
        </svg>
    );
}

function ClockIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
        </svg>
    );
}

function WalkIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="5" r="1" />
            <path d="m9 20 3-6 3 6" />
            <path d="M6 8h6l2 4" />
        </svg>
    );
}

function SparkleIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
            <circle cx="12" cy="12" r="4" />
        </svg>
    );
}

function AIIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" />
            <circle cx="8" cy="14" r="1" />
            <circle cx="16" cy="14" r="1" />
        </svg>
    );
}
